/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import React from "react";
import { Link } from 'react-router';
import { useTranslation } from "react-i18next";
import { Title, Text, Badge, Stack, Group } from "@/shared/ui";
import { cn } from "@/shared/ui/utils/tailwind";
import PolicyPageLayout from "../service-terms/PolicyPageLayout";
import { getDocumentConfig, type DocumentType } from "@shared/utils/documentVersions";

interface DocumentVersionListProps {
  documentType: DocumentType;
  i18nPrefix: string;
}

const versionLinkClass = cn(
  'no-underline text-inherit',
  'rounded-(--radius-lg) p-(--space-4) -mx-4 block',
  'transition-colors duration-150 ease-in-out',
  'hover:bg-(--surface-secondary)',
);

/**
 * Shared component for rendering document version lists.
 * Parameterized by document type and i18n prefix to avoid duplication
 * across service-terms, privacy-policy, cookie-policy, and data-storage-terms.
 */
const DocumentVersionList: React.FC<DocumentVersionListProps> = ({ documentType, i18nPrefix }) => {
  const { t } = useTranslation("@pages/policy");
  const config = getDocumentConfig(documentType);
  const latestVersion = config.versions[config.versions.length - 1]?.version;

  return (
    <PolicyPageLayout
      title={t(`${i18nPrefix}.title`)}
      lastUpdatedLabel={t(`${i18nPrefix}.lastUpdated`)}
      versionLabel={t(`${i18nPrefix}.version`)}
    >
      <Stack gap="var(--space-4)">
        <Text>{t(`${i18nPrefix}.versionListDescription`, "Select a version to view:")}</Text>

        {config.versions.map((versionInfo) => (
          <Link
            key={versionInfo.version}
            to={`${config.basePath}/v${versionInfo.version}`}
            className={versionLinkClass}
          >
            <Stack gap="var(--space-2)">
              <Group gap="var(--space-2)" align="center">
                <Title order={3}>
                  {t(`${i18nPrefix}.version`)} {versionInfo.version}
                </Title>
                {versionInfo.version === latestVersion && (
                  <Badge variant="light">{t(`${i18nPrefix}.latest`)}</Badge>
                )}
              </Group>
              <Group gap="var(--space-4)">
                <Text className="text-(--text-secondary)">
                  <strong>{t(`${i18nPrefix}.published`)}:</strong>{" "}
                  {versionInfo.published}
                </Text>
                <Text className="text-(--text-secondary)">
                  <strong>{t(`${i18nPrefix}.lastUpdated`)}:</strong>{" "}
                  {versionInfo.lastUpdated}
                </Text>
              </Group>
            </Stack>
          </Link>
        ))}
      </Stack>
    </PolicyPageLayout>
  );
};

export default DocumentVersionList;
